import { Matrix4, Vector2, Vector3 } from "three";

import SceneObjectData from "./SceneObjectData";
import SceneObjectTypeEnum from "./SceneObjectTypeEnum";
import Placement3d from "../Placement3d";
import { vector3ToJson } from "../../services/bimVisualizerApi/primitivesSerialization";
import { UUID } from "../../utils/typeDefinitions";
import Constants from "../../constants";
import { mmFromSceneUnits } from "../../utils/scaler";
import { applyOnlyRotation } from "../../utils/transformUtils";
import { calculatePolygonAreaAndCentroid, convexHull2d } from "../../utils/mathUtils";
import EditHandleTypeEnum from "../../enums/EditHandleTypeEnum";
import { DragMeasurement, formatCm } from "../../utils/dragMeasurement";

// Shared base for any annotation whose visual is an extruded closed loop (Polygon/Rectangle/Circle
// today). Holds the fields and behavior common to all of them; each subclass supplies its own
// native shape (points / corners / radius) via getLoopPoints3d() and its own exact area formula
// via computeEnclosedArea().
abstract class ExtrudedLoopAnnotationData extends SceneObjectData {
    // Font size (scene units) applied to the area label when the enclosed area is at or above
    // SMALL_AREA_THRESHOLD m², vs. below it. Chosen once when the annotation is created
    // (see AddExtrudedLoopAnnotationCmd) rather than continuously recalculated, so a later edit
    // through the properties panel isn't fought by the area heuristic, and the properties panel
    // always shows the size actually rendered.
    static readonly DEFAULT_FONT_SIZE = 0.07; // 7cm
    static readonly SMALL_FONT_SIZE = 0.05; // 5cm
    static readonly SMALL_AREA_THRESHOLD = 0.1; // m²

    // Below this enclosed area, the area label defaults to hidden when the annotation is created
    // (see applyAreaBasedShowAreaText()) — a label would be more clutter than useful information
    // on such a small shape. Purely a creation-time default; editable afterwards via the
    // properties panel like any other showAreaText change.
    static readonly MIN_AREA_FOR_SHOWN_TEXT = 0.05; // m²

    placement: Placement3d;
    depth: number;
    extrudedDirection: Vector3;
    fontSize: number;
    showAreaText: boolean;
    showVolume: boolean;

    constructor(    type: SceneObjectTypeEnum,
                    placement: Placement3d,
                    depth: number,
                    extrudedDirection: Vector3,
                    fontSize: number,
                    showAreaText: boolean = true,
                    showVolume: boolean = false,
                    uuid?: UUID,
                    parentId?: UUID) {
        super(type, uuid, parentId);
        this.placement = placement;
        this.depth = depth;
        this.extrudedDirection = extrudedDirection;
        this.fontSize = fontSize;
        this.showAreaText = showAreaText;
        this.showVolume = showVolume;
    }

    // Exact enclosed volume: enclosed area times the (unsigned) extrusion depth. Depth's sign only
    // indicates which side of the profile the extrusion points to (see the "negative depth" note
    // elsewhere) — it doesn't change the volume itself.
    computeEnclosedVolume(): number {
        return this.computeEnclosedArea() * Math.abs(this.depth);
    }

    // Shared by every extruded shape, so subclasses handling their own profile handles must fall
    // through to super for this one. Signed, unlike the volume above: a negative depth means the
    // extrusion flipped to the other side of the profile, which is worth seeing while dragging.
    getDragMeasurement(handleType: EditHandleTypeEnum): DragMeasurement | undefined {
        if (handleType !== EditHandleTypeEnum.EXTRUSION_DEPTH) return super.getDragMeasurement(handleType);

        return { labelKey: 'properties.field.extrusionDepth.name', text: formatCm(this.depth) };
    }

    // Exact enclosed area of the shape. Deliberately NOT derived from getProfilePoints2d() here:
    // AreaAnnotationData's polygon points aren't guaranteed to actually lie in the plane implied by
    // placement.refDirection/axis (see AreaAnnotationData.test.ts), so a shared shoelace-over-
    // projected-points implementation would silently return 0 for such (valid) polygons. Each
    // subclass computes this natively instead (Newell's method for Polygon, width*height for
    // Rectangle, pi*r^2 for Circle).
    abstract computeEnclosedArea(): number;

    // The shape's native closed-loop offsets, relative to placement.location, expressed in world
    // axes (same convention as AreaAnnotationData.points always used).
    protected abstract getLoopPoints3d(): Vector3[];

    // Loop points projected into the placement's local 2D basis. Used by the geometry layer for
    // edge-drawing and centroid computation only — never for computeEnclosedArea() (see above).
    getProfilePoints2d(): Vector2[] {
        const localX = this.placement.refDirection.clone().normalize();
        const localY = this.placement.axis.clone().cross(localX).normalize();
        return this.getLoopPoints3d().map(p => new Vector2(p.dot(localX), p.dot(localY)));
    }

    // Centroid of the top face, in the same local frame as placement.location (i.e. before the
    // owning scene object's own world transform is applied — matches the convention every
    // subclass's getEditHandles() already uses for its own handle positions).
    getTopFaceCentroid3d(): Vector3 {
        const points2d = this.getProfilePoints2d();
        if (points2d.length < 3) return this.placement.location.clone();

        const localX = this.placement.refDirection.clone().normalize();
        const localY = this.placement.axis.clone().cross(localX).normalize();
        const { centroid } = calculatePolygonAreaAndCentroid(points2d);

        const offset = localX.multiplyScalar(centroid.x)
            .add(localY.multiplyScalar(centroid.y))
            .add(this.placement.axis.clone().multiplyScalar(this.depth));

        return this.placement.location.clone().add(offset);
    }

    // A true top-down (world XY) projection of the full extruded solid — offsets relative to
    // placement.location's X/Y, ignoring Z entirely. Unlike getProfilePoints2d() (which projects
    // into the placement's OWN local basis — correct for rendering the shape in its real 3D
    // orientation, e.g. flush against a wall), this is for the 2D plan-view geometry, which must
    // show a genuine bird's-eye silhouette regardless of the shape's placement: a flat profile
    // placed on a non-horizontal surface (e.g. a wall) would otherwise render edge-on as a
    // degenerate line when viewed from directly above. Combines both the bottom (z=0) and top
    // (z=depth) loop vertices — the extrusion itself can contribute to the top-down footprint
    // whenever placement.axis isn't purely vertical (e.g. a wall-mounted annotation extruded
    // outward) — then takes their convex hull (see convexHull2d() for why hull, not exact outline).
    // Exception: when the profile lies in the XY plane (floor/ceiling), the extrusion is vertical
    // and adds nothing to the footprint, so the profile itself IS the exact silhouette — return it
    // as-is. Hulling it would silently drop every reflex vertex of a concave profile (e.g. the
    // inner corner of an L-shaped area).
    getTopDownProjectionPoints2d(): Vector2[] {
        const bottom = this.getLoopPoints3d();
        if (Math.abs(this.placement.axis.z) > Constants.HORIZONTAL_SURFACE_DOT_THRESHOLD) {
            return bottom.map(p => new Vector2(p.x, p.y));
        }

        const depthOffset = this.placement.axis.clone().multiplyScalar(this.depth);
        const top = bottom.map(p => p.clone().add(depthOffset));

        const projected = [...bottom, ...top].map(p => new Vector2(p.x, p.y));
        return convexHull2d(projected);
    }

    applyAreaBasedFontSize(): void {
        this.fontSize = this.computeEnclosedArea() < ExtrudedLoopAnnotationData.SMALL_AREA_THRESHOLD
            ? ExtrudedLoopAnnotationData.SMALL_FONT_SIZE
            : ExtrudedLoopAnnotationData.DEFAULT_FONT_SIZE;
    }

    // Recomputed on every point placed/dragged during creation (same as applyAreaBasedFontSize()),
    // so shrinking/growing the shape before it's finished doesn't get stuck with a stale decision —
    // only the value baked in at the moment the creation command finishes actually sticks.
    applyAreaBasedShowAreaText(): void {
        this.showAreaText = this.computeEnclosedArea() >= ExtrudedLoopAnnotationData.MIN_AREA_FOR_SHOWN_TEXT;
    }

    // Rotates the fields common to every subclass. Subclasses call this from their own
    // applyMatrix4() before rotating their own shape-specific offset fields.
    protected applyMatrix4Base(matrix: Matrix4): void {
        this.placement.location.applyMatrix4(matrix);
        applyOnlyRotation(this.placement.refDirection, matrix);
        applyOnlyRotation(this.placement.axis, matrix);
        applyOnlyRotation(this.extrudedDirection, matrix);
    }

    // Serializes the fields common to every subclass. Subclasses spread this from their own
    // toJson() before adding their own shape-specific fields.
    protected toJsonBase(): any {
        return {
            ...super.toJson(),
            placement: {
                location: vector3ToJson(this.placement.location, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits),
                refDirection: vector3ToJson(this.placement.refDirection, Constants.IO_DIRECTION_PRECISION),
                axis: vector3ToJson(this.placement.axis, Constants.IO_DIRECTION_PRECISION)
            },
            depth: Math.round(mmFromSceneUnits(this.depth) * 1000) / 1000,
            extrudedDirection: vector3ToJson(this.extrudedDirection, Constants.IO_DIRECTION_PRECISION),
            fontSize: mmFromSceneUnits(this.fontSize),
            showAreaText: this.showAreaText,
            showVolume: this.showVolume
        };
    }
}

export default ExtrudedLoopAnnotationData;
